// Ручка ресайза боковой панели (desktop). Тонкая вертикальная полоса у границы
// панели — pointerdown тянет границу, меняя ширину.
//
// По умолчанию (без пропсов) работает ровно как раньше: панель у ПРАВОГО края,
// ширина в общем `useSidePanelWidthStore` — это сохраняет поведение обоих
// существующих потребителей (`side-panel.tsx`, `tutor-side-panel.tsx`).
// Панели воркспейса курса передают `side` / `onWidth` / `onReset` / `offset`.
//
// pointer events (не mouse): один путь для мыши и трекпада. Реального
// setPointerCapture нет — capture эмулируется слушателями на window (pointermove /
// pointerup / pointercancel). На время drag глушим user-select и ставим
// col-resize на body, чтобы курсор не «прыгал» при выходе за пределы ручки.
//
// Только desktop (`hidden lg:block`): на mobile панели = шторки, ресайз мышью не
// нужен.
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { cn } from "@/lib/cn";
import { useSidePanelWidthStore } from "@/stores/side-panel-width";

/** Идёт ли прямо сейчас drag какой-либо ручки (одна вкладка = один drag). */
let resizeActive = false;
const resizeListeners = new Set<() => void>();

function setResizeActive(next: boolean): void {
	if (resizeActive === next) return;
	resizeActive = next;
	if (typeof document !== "undefined") {
		// data-атрибут на <html> — точка опоры для CSS/E2E и для порталов
		// (Radix/vaul), которые не видят React-контекст.
		if (next) document.documentElement.dataset.resizing = "1";
		else delete document.documentElement.dataset.resizing;
	}
	for (const listener of resizeListeners) listener();
}

function subscribeResize(listener: () => void): () => void {
	resizeListeners.add(listener);
	return () => {
		resizeListeners.delete(listener);
	};
}

/**
 * Активен ли drag ручки ресайза.
 *
 * Потребители гасят на это время layout-переходы (`transition-[margin,padding]`
 * у `<main>`, `transition-[width]` у панелей) — иначе контент «резиново»
 * отстаёт от курсора на 200 мс.
 */
export function usePanelResizeActive(): boolean {
	return useSyncExternalStore(
		subscribeResize,
		() => resizeActive,
		() => false,
	);
}

export interface PanelResizeHandleProps {
	/**
	 * Ось перетаскивания. `vertical` (по умолчанию) — тонкая полоса у боковой
	 * границы панели, меняет ШИРИНУ. `horizontal` — полоса на границе двух
	 * блоков одной колонки, меняет их пропорцию по высоте (витрина инструментов
	 * ↔ список артефактов). Общий компонент, а не копия: drag-механика (эмуляция
	 * capture, глушение выделения, курсор на body, двойной клик = сброс) одна на
	 * обе оси, и расходиться ей нельзя.
	 */
	orientation?: "vertical" | "horizontal";
	/** У какого края экрана живёт панель. По умолчанию — правый (текущее поведение). */
	side?: "left" | "right";
	/** Новая ширина панели в px (уже посчитана от курсора; clamp — на стороне стора). */
	onWidth?: (width: number) => void;
	/**
	 * Горизонтальная ось: сырая координата курсора по Y. Пропорцию считает
	 * потребитель — только он знает геометрию своего контейнера.
	 */
	onDragY?: (clientY: number) => void;
	/** Двойной клик по ручке — сброс ширины на дефолт. */
	onReset?: () => void;
	/** Для `side="left"`: смещение левого края панели от края окна (ширина рельса). */
	offset?: number;
	className?: string;
}

export function PanelResizeHandle({
	orientation = "vertical",
	side = "right",
	onWidth,
	onDragY,
	onReset,
	offset = 0,
	className,
}: PanelResizeHandleProps = {}) {
	// Дефолт сохраняет прежнее поведение: глобальный стор ширины панелей оболочки.
	// Селекторы возвращают стабильные ссылки, лишних ре-рендеров нет.
	const storeSetWidth = useSidePanelWidthStore((s) => s.setWidth);
	const storeResetWidth = useSidePanelWidthStore((s) => s.resetWidth);
	const applyWidth = onWidth ?? storeSetWidth;
	const applyReset = onReset ?? storeResetWidth;

	// Слушатели window живут только во время активного drag — ссылки для cleanup.
	const moveRef = useRef<((e: PointerEvent) => void) | null>(null);
	const upRef = useRef<((e: PointerEvent) => void) | null>(null);
	// Метка времени предыдущего pointerdown — детект двойного клика по ручке.
	const lastDownRef = useRef(0);

	const detach = useCallback(() => {
		if (moveRef.current) window.removeEventListener("pointermove", moveRef.current);
		if (upRef.current) {
			window.removeEventListener("pointerup", upRef.current);
			// pointercancel обязателен: системный жест / прерывание touch иначе
			// оставляет слушатели и `cursor: col-resize` на body до размонтирования.
			window.removeEventListener("pointercancel", upRef.current);
		}
		moveRef.current = null;
		upRef.current = null;
		document.body.style.userSelect = "";
		document.body.style.cursor = "";
		setResizeActive(false);
	}, []);

	// Снять слушатели при размонтировании (если панель закрылась прямо во время drag).
	useEffect(() => detach, [detach]);

	const handlePointerDown = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			e.preventDefault();

			// Двойной клик по ручке (два pointerdown < 300ms) → сброс ширины на дефолт.
			// Детектим вручную: preventDefault выше подавляет синтезированный dblclick.
			const now = e.timeStamp;
			if (now - lastDownRef.current < 300) {
				lastDownRef.current = 0;
				applyReset();
				return;
			}
			lastDownRef.current = now;

			document.body.style.userSelect = "none";
			document.body.style.cursor = orientation === "horizontal" ? "row-resize" : "col-resize";
			setResizeActive(true);

			const onMove = (ev: PointerEvent) => {
				if (orientation === "horizontal") {
					// Пропорцию считает потребитель: только он знает высоту и верх
					// своего контейнера.
					onDragY?.(ev.clientY);
					return;
				}
				// Панель у правого края → ширина = расстояние от курсора до правого
				// края окна; у левого → от левого края панели (offset = рельс).
				// clamp делает вызывающая сторона (стор), как и раньше.
				applyWidth(side === "left" ? ev.clientX - offset : window.innerWidth - ev.clientX);
			};
			const onUp = () => detach();

			moveRef.current = onMove;
			upRef.current = onUp;
			window.addEventListener("pointermove", onMove);
			window.addEventListener("pointerup", onUp);
			window.addEventListener("pointercancel", onUp);
		},
		[detach, applyWidth, applyReset, side, offset, orientation, onDragY],
	);

	// Декоративная drag-ручка (не семантический slider): ширину панели меняют
	// мышью, клавиатурного эквивалента нет → role/tabIndex не навешиваем.
	// ЭТО ОСОЗНАННОЕ РЕШЕНИЕ (план §4.7 п.7, не регресс): `role="separator"`
	// тянет за собой aria-valuenow/valuemin/valuemax и клавиатурное управление,
	// тогда как сворачивание/разворачивание панелей уже доступно и кнопкой в
	// шапке, и хоткеем. aria-hidden снимает interactions-предупреждение.
	return (
		<div
			aria-hidden="true"
			data-panel-resize-handle={orientation === "horizontal" ? "horizontal" : side}
			onPointerDown={handlePointerDown}
			className={cn(
				"absolute z-10 hidden transition-colors duration-fast lg:block",
				"hover:bg-primary/30 active:bg-primary/40",
				orientation === "horizontal"
					? "inset-x-0 top-0 h-1.5 cursor-row-resize"
					: cn("inset-y-0 w-1.5 cursor-col-resize", side === "left" ? "right-0" : "left-0"),
				className,
			)}
		/>
	);
}
